/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import ScheduleLiveConsultation from '@brands/Dashboard/Dashboard/DashboardModals/ScheduleLiveConsultation';
import { convertDateFormat } from '@brands/Dashboard/MyProfile/Patient/utils/utilsFunctions';
import { SelectedAppointment } from '@brands/Dashboard/PatientForms/PatientForm/utils/types';
import ProviderProfileCardModal from '@brands/Dashboard/ProfileCards/Provider/ProfileCards';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import {
  AvailabilitySlotDay,
  listProviderAvailabilityTimeSlots,
} from '@brands/services/availability/listProviderAvailabilities';
import { rescheduleCase, RescheduleCasePayload } from '@brands/services/cases/rescheduleCase';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import { getPricings } from '@brands/services/identity/getPricings';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { Language, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectCurrentPatientState } from '@brands/store/selectors/currentPatientState';
import { selectOrganizationPrices } from '@brands/store/selectors/organizationPrices';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { setOrganizationPrices } from '@brands/store/slices/organizationPricingSlice';
import { setForm } from '@brands/store/slices/patientFormSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { shouldShowDoctorTitle } from '@brands/Utils/getProviderTitle';
import { Button } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import Moment from 'moment';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { FaChevronLeft } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Container from '../../../../Components/Container/Container';
import { useAppSelector } from '../../../../hooks/useReduxHook';
import useWindowSize from '../../../../hooks/useWindowSize';
import styles from './styles.module.scss';
import { formatTime } from './utils/utils';

const MAX_DAYS = 14;

const SelectProviderDetails = (): JSX.Element => {
  const { state } = useLocation();

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { userInfo } = useAppSelector(selectAuth);
  const { values, selectedPatient } = useAppSelector(selectPatientForm);
  const { selectedPatient: currentPatient } = useAppSelector(selectPatient);
  const organizationPrices = useAppSelector(selectOrganizationPrices);
  const RescheduleCase = state?.RescheduleCase ? state.RescheduleCase : searchParams.get('reschedule') || null;
  const patient_current_state = state?.patient_current_state
    ? state?.patient_current_state
    : searchParams.get('case_state') || null;
  const screenSize = useWindowSize();
  const roleNavigate = useRoleNavigate();
  const [loading, setLoading] = useState(false);
  const [providerProfileCardModal, setProviderProfileCardModal] = useState<boolean>(false);
  const [providerParticipant, setProviderParticipant] = useState<any>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<SelectedAppointment>({
    selectedDate: '',
    selectedTimeSlot: '',
    selectedDateTime: '',
    selectedProviderId: undefined,
  });
  const [rescheduleSuccessModal, setRescheduleSuccessModal] = useState<{
    openModal: boolean;
    updatedCase: ICase | null;
  }>({
    openModal: false,
    updatedCase: null,
  });
  const [availabilityDays, setAvailabilityDays] = useState<AvailabilitySlotDay[]>([]);
  const [visitDate, setVisitDate] = useState<string>('');
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true); // Default true
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [manualDateSelected, setManualDateSelected] = useState(false);
  const currentState: Record<number, string> = useAppSelector(selectCurrentPatientState);
  const isAdmin =
    userInfo.role.name === UserRoleName.Admin ||
    userInfo.role.name === UserRoleName.SuperAdmin ||
    userInfo.role.name === UserRoleName.OrganizationAdmin;
  const isStaffPickingForPatient =
    isAdmin || userInfo.role.name === UserRoleName.Collector || userInfo.role.name === UserRoleName.CareAdvisor;

  const licenseState = useMemo(() => {
    if (RescheduleCase && patient_current_state) {
      return patient_current_state;
    }
    const currentPatientId = currentPatient?.id ?? Number(selectedPatient?.value);
    if (isStaffPickingForPatient) {
      return currentPatientId ? currentState[currentPatientId] || '' : '';
    }
    // Not yet confirmed: never assume a real state for provider licensing —
    // an empty license_states filter is a safe "no match yet" (matches the
    // isStaffPickingForPatient branch above), not a silent guess.
    return currentState[userInfo.id] || '';
  }, [
    RescheduleCase,
    currentPatient?.id,
    currentState,
    isStaffPickingForPatient,
    patient_current_state,
    selectedPatient?.value,
    userInfo.id,
  ]);

  const provider = useMemo(() => {
    if (state?.provider_details) {
      return state.provider_details;
    }
    return values.find((item) => item.form_question_id === 14)?.value.selectedProvider || null;
  }, [state, values]);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    (async (): Promise<void> => {
      const getPricingsFn = async (): Promise<void> => {
        try {
          const pricings = await getPricings();
          dispatch(setOrganizationPrices({ currentOrganizationPrices: pricings }));
        } catch (error: unknown) {
          displayErrorDetails(error);
        }
      };

      const listAvailabilityTimeSlotsParams = {
        tz: timezone,
      };

      await listProviderAvailabilityTimeSlots(provider?.id || 0, listAvailabilityTimeSlotsParams)
        .then((response) => {
          const days = response.days || [];
          setAvailabilityDays(days);
          setEndCursor(response.end_cursor || null);
          setHasNextPage(response.has_next_page && availabilityDays.length + days.length < MAX_DAYS);
        })
        .catch((error: unknown) => {
          displayErrorDetails(error);
          setAvailabilityDays([]);
        });

      // Staff land here from linked-case notifications too, and the backend
      // refuses /pricings to anyone but a Patient ([12600-2]).
      if (userInfo.role.name === UserRoleName.Patient && !organizationPrices?.currentOrganizationPrices) {
        await getPricingsFn();
      }
    })();
  }, []);

  const caseType = useMemo(() => {
    return (
      (RescheduleCase
        ? state.caseType
        : (values.find((item) => item.form_question_id === 20)?.value.caseType as string)) ||
      CaseType.video_call_scheduled
    );
  }, [RescheduleCase, state, values]);

  const onSubmit = async (selectedAppointment: SelectedAppointment): Promise<void> => {
    if (selectedAppointment.selectedTimeSlot) {
      if (RescheduleCase && state?.caseId) {
        let updatedCase;
        try {
          setLoading(true);
          const payload: RescheduleCasePayload = {
            video_conference_date: convertDateFormat(`${selectedAppointment.selectedDateTime as string}`),
            clinician_id: selectedAppointment.selectedProviderId,
            ...(caseType === CaseType.oral_health_coaching ? { case_type: caseType } : {}),
          };
          updatedCase = await rescheduleCase(state?.caseId, payload);

          setRescheduleSuccessModal({
            openModal: true,
            updatedCase: updatedCase as ICase,
          });
          setLoading(false);
        } catch (error: unknown) {
          displayErrorDetails(error);
        }
      } else {
        const filteredPayload = values.filter((item) => item.form_question_id !== 14);

        filteredPayload.push({
          form_question_id: 14,
          value: {
            selectedTimeSlot: selectedAppointment.selectedDateTime as string,
            selectedProvider: provider,
            ...(caseType !== CaseType.oral_health_coaching && {
              selectedProviderId: selectedAppointment.selectedProviderId,
            }),
            ...(caseType === CaseType.oral_health_coaching && {
              selectedCareAdvisorId: selectedAppointment.selectedProviderId,
            }),
          },
        });
        dispatch(setForm(filteredPayload));
        roleNavigate('/clinical-data', {
          state: {
            caseType: 'video_call_scheduled',
            patientId: selectedPatient?.value,
          },
        });
      }
    } else {
      roleNavigate('/select-provider-details', {
        state: {
          caseType,
          patientId: selectedPatient?.value,
          provider_id: selectedTimeSlot.selectedProviderId,
        },
      });
    }
  };

  const handleTimeSlotClick = async (date: string, slot: string, provider_id: number): Promise<void> => {
    const currentTime = Moment(); // Get the current time
    const startTimeMoment = Moment(slot);
    const isCurrentDate = startTimeMoment.isSame(moment(), 'day');

    if (isCurrentDate) {
      const isPast = startTimeMoment.isBefore(currentTime);
      if (isPast) {
        toast.error('The time slot you selected is in the past. Please select a different time slot.', {
          className: 'fs-unmask',
        });
        return;
      }
    }

    if ((startTimeMoment.diff(currentTime, 'minutes') >= 30 && isCurrentDate) || !isCurrentDate) {
      const formattedForDisplay = startTimeMoment.format('dddd, MMMM D, YYYY h:mm a'); // 👈 For storing in Redux
      const formattedForSubmission = formatTime(startTimeMoment.toDate());
      await setSelectedTimeSlot({
        selectedDate: date,
        selectedTimeSlot: formattedForSubmission,
        selectedDateTime: formattedForDisplay,
        selectedProviderId: provider_id,
      });
      onSubmit({
        selectedDate: date,
        selectedTimeSlot: formattedForSubmission,
        selectedDateTime: formattedForDisplay,
        selectedProviderId: provider_id,
      });
    } else {
      toast.error('The time slot you selected is no longer available. Please select a different time slot.', {
        className: 'fs-unmask',
      });
      await setSelectedTimeSlot({
        selectedDate: '',
        selectedTimeSlot: '',
        selectedDateTime: '',
        selectedProviderId: undefined,
      });
    }
  };

  const goBackToSelectProvider = (): void => {
    dispatch(setForm(values.filter((item) => item.form_question_id !== 14)));

    roleNavigate('/select-provider', {
      state: {
        caseType,
        patientId: selectedPatient?.value,
        provider_id: selectedTimeSlot.selectedProviderId,
        RescheduleCase: state?.RescheduleCase,
        caseId: state?.caseId,
      },
    });
  };

  const loadAvailabilityDays = async (cursorParam: string | null = null): Promise<void> => {
    if (!provider?.id || isLoadingMore || !hasNextPage || availabilityDays.length >= MAX_DAYS) return;

    try {
      setIsLoadingMore(true);
      const response = await listProviderAvailabilityTimeSlots(provider.id, {
        tz: timezone,
        limit: Math.min(3, MAX_DAYS - availabilityDays.length), // Only fetch remaining days
        cursor: cursorParam || undefined,
      });

      const days = response.days || [];
      setAvailabilityDays((prev) => {
        if (manualDateSelected && visitDate) {
          // Overwrite with only the manually selected day's data
          return days.filter((day) => day.date === visitDate);
        }
        // Otherwise, merge new days into the existing list, avoiding duplicates
        return [...prev, ...days.filter((day) => !prev.some((d) => d.date === day.date))];
      });

      setEndCursor(response.end_cursor || null);
      setHasNextPage(response.has_next_page && availabilityDays.length + days.length < MAX_DAYS);
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (loading) {
    return <Loading dotted />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: '24px',
        paddingBottom: '0px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.providerSelectContainer}`}>
        <PatientIntakeNavigation progress={12} />
        <form className={`${styles.formContainer}`}>
          <div className={styles.providerSelectWrapper}>
            <div className={styles.providerSelectTitleContainer}>
              <div className={styles.providerSelectTitle}>Select a timeslot</div>
              {screenSize.width >= 768 && <div className={styles.providerSelectSubtitle}>Times are in {timezone}</div>}
            </div>

            <div className={styles.providerSelect}>
              <div className={styles.providerSelectItemDetails}>
                <div
                  className={styles.imageContainer}
                  onClick={() => {
                    setProviderParticipant(provider.user);
                    setProviderProfileCardModal(true);
                  }}
                >
                  <img
                    alt="Avatar"
                    className={`fs-exclude ${styles.providerImage}`}
                    src={getUserAvatar(provider?.id || 0)}
                    onError={(e) => {
                      e.currentTarget.src = '/assets/images/default-avatar.svg';
                    }}
                  />
                </div>

                <div className={styles.providerSelectItemDetailsTextContainer}>
                  <div className={styles.providerSelectItemDetailsText}>
                    <div
                      className={`fs-exclude ${styles.providerName}`}
                      onClick={() => {
                        setProviderParticipant(provider.user);
                        setProviderProfileCardModal(true);
                      }}
                    >
                      {shouldShowDoctorTitle(provider.user?.provider_profile?.credentials) ? 'Dr. ' : ''}
                      {provider.user.full_name}, {provider.user?.provider_profile?.credentials || 'Unknown Title'}
                    </div>

                    <div className={styles.providerLanguages}>
                      <span className={styles.providerLanguagesLabel}>Languages: </span>
                      <span className={styles.providerLanguages}>
                        {provider.user?.basic_info?.languages?.length
                          ? provider.user.basic_info.languages.map((lang: Language) => lang?.name).join(', ')
                          : 'No languages available'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.providerSelectVisitDate}>
                <div className={styles.providerSelectVisitDateLabel}>Visit Date</div>
                <div className={styles.providerSelectVisitDateInput}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      format="MM/DD/YYYY"
                      value={visitDate ? dayjs(visitDate) : null}
                      minDate={dayjs()}
                      maxDate={dayjs().add(1, 'year')}
                      onChange={(newValue) => {
                        const year = newValue?.year();
                        if (newValue?.isValid() && year && year >= 1000 && year <= 9999) {
                          const today = dayjs();
                          const nextYear = today.add(1, 'year');

                          // Extend dayjs for comparison helpers
                          import('dayjs/plugin/isSameOrAfter').then((plugin) => {
                            dayjs.extend(plugin.default);
                            import('dayjs/plugin/isSameOrBefore').then((plugin2) => {
                              dayjs.extend(plugin2.default);

                              if (newValue.isSameOrAfter(today, 'day') && newValue.isSameOrBefore(nextYear, 'day')) {
                                const selectedDate = newValue.format('YYYY-MM-DD');
                                setManualDateSelected(true);
                                setVisitDate(selectedDate);
                                setSelectedTimeSlot({
                                  selectedDate,
                                  selectedTimeSlot: '',
                                  selectedDateTime: '',
                                  selectedProviderId: provider.id,
                                });

                                const listAvailabilityTimeSlotsParams = {
                                  tz: timezone,
                                  date: selectedDate,
                                };

                                listProviderAvailabilityTimeSlots(provider.id, listAvailabilityTimeSlotsParams)
                                  .then((response) => {
                                    const days = response.days || [];
                                    setAvailabilityDays(days);
                                    setEndCursor(response.end_cursor || null);
                                    setHasNextPage(
                                      response.has_next_page && availabilityDays.length + days.length < MAX_DAYS,
                                    );
                                  })
                                  .catch((error: unknown) => {
                                    displayErrorDetails(error);
                                    setAvailabilityDays([]);
                                  });
                              } else {
                                displayErrorDetails('Please select a date between today and one year from now.');
                              }
                            });
                          });
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          className: styles.providerSelectVisitDateInputField,
                          inputProps: {
                            readOnly: false,
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>
              {availabilityDays.map((day, index) => {
                return (
                  <div key={index} className={styles.providerSelectItemTimeSlots}>
                    <div className={styles.providerSelectItemTimeSlotsLabel}>
                      {day.date_label || Moment(day.date).format('dddd, MMMM D, YYYY')}
                    </div>
                    <div className={styles.providerSelectItemTimeSlotContainer}>
                      {day.sync_time_slots.length > 0 ? (
                        day.sync_time_slots?.map((slot: string, i: number) => {
                          const slotStr = Moment(slot).format();
                          return (
                            <Button
                              key={i}
                              color="primary"
                              variant="outlined"
                              className={`${styles.providerSelectItemTimeSlot} ${
                                selectedTimeSlot.selectedDate === day.date &&
                                selectedTimeSlot.selectedTimeSlot === formatTime(Moment(slot).toDate()) &&
                                selectedTimeSlot.selectedProviderId === provider.id
                                  ? styles.selectedTimeSlot
                                  : ''
                              }`}
                              onClick={() => handleTimeSlotClick(day.date, slotStr, provider.id)}
                            >
                              {Moment(slotStr).format('h:mma')}
                            </Button>
                          );
                        })
                      ) : (
                        <div className={styles.noTimeSlots}>No available appointments</div>
                      )}
                    </div>
                  </div>
                );
              })}
              {hasNextPage && endCursor && (
                <span className={styles.seeMore} onClick={() => loadAvailabilityDays(endCursor)}>
                  See more openings
                </span>
              )}
            </div>

            <div className={styles.providerSelectFooter}>
              <button
                type="button"
                className={styles.providerSelectFooterBackButton}
                onClick={() => goBackToSelectProvider()}
              >
                <FaChevronLeft />
                Back
              </button>
            </div>
          </div>
        </form>
        {providerProfileCardModal && (
          <ProviderProfileCardModal
            isOpen={providerProfileCardModal}
            toggleModal={setProviderProfileCardModal}
            providerIdentity={providerParticipant}
            patientCurrentStateValue={licenseState}
          />
        )}
        {isLoadingMore && <div className={styles.loadingMore}>Loading more days...</div>}
        {rescheduleSuccessModal.openModal && (
          <ScheduleLiveConsultation newCase={rescheduleSuccessModal.updatedCase as ICase} isRescheduled />
        )}
      </div>
    </Container>
  );
};
export default SelectProviderDetails;
